import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Heading,
  VStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useColorModeValue,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { fmtIsoWithOffset } from "../../utils/dates";

// Define the expected job structure
interface Job {
  job_id: string;
  job_name: string;
  next_run_time: string | null;
  args: any[];
}

interface ThreadStatus {
    scheduler_running: boolean;
    apscheduler_thread_found: boolean;
    apscheduler_thread_alive: boolean;
    apscheduler_thread_name: string | null;
  }
  
  interface ApiResponse {
    thread_status: ThreadStatus;
    jobs: Job[];
  }
  
  const BackgroundJobsView: React.FC = () => {
    const [jobs, setJobs] = useState<Job[]>([]);
    const [threadStatus, setThreadStatus] = useState<ThreadStatus | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const tableHeaderBg = useColorModeValue("gray.50", "gray.700");
  
    const fetchJobs = async () => {
      try {
        const api = process.env.REACT_APP_API_URL || 'https://default-url.com';
        const response = await axios.get<ApiResponse>(`${api}/scheduler/jobs`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
  
        setJobs(response.data.jobs);
        setThreadStatus(response.data.thread_status);
      } catch (err) {
        setError('Failed to load background jobs');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
  
    useEffect(() => {
      fetchJobs();
    }, []);
  
    return (
      <Box as="main" mx="auto" p={8} maxW="1200px">
        <VStack spacing={4} mb={6}>
          <Heading size="lg">Scheduled Background Jobs</Heading>
          {loading && <Spinner />}
          {error && <Text color="red.500">{error}</Text>}
  
          {threadStatus && (
            <Box bg="gray.50" p={4} rounded="md" shadow="md" width="100%">
              <Text><strong>Scheduler Running:</strong> {threadStatus.scheduler_running ? 'Yes' : 'No'}</Text>
              <Text><strong>Thread Found:</strong> {threadStatus.apscheduler_thread_found ? 'Yes' : 'No'}</Text>
              <Text><strong>Thread Alive:</strong> {threadStatus.apscheduler_thread_alive ? 'Yes' : 'No'}</Text>
              <Text><strong>Thread Name:</strong> {threadStatus.apscheduler_thread_name || 'N/A'}</Text>
            </Box>
          )}
        </VStack>
  
        {!loading && jobs.length > 0 ? (
          <Table variant="simple">
            <Thead bg={tableHeaderBg}>
              <Tr>
                <Th>Job ID</Th>
                <Th>Job Name</Th>
                <Th>Next Run Time</Th>
                <Th>Args</Th>
              </Tr>
            </Thead>
            <Tbody>
              {jobs.map((job) => (
                <Tr key={job.job_id}>
                  <Td>{job.job_id}</Td>
                  <Td>{job.job_name}</Td>

                  <Td>
                   {/*  {job.next_run_time ? fmtLocal(job.next_run_time, { hour: undefined, minute: undefined }) : 'N/A'} */}
                    {job.next_run_time
                      ? fmtIsoWithOffset(job.next_run_time, { second: undefined })
                      : 'N/A'}
                  </Td>
                  <Td>
                    {job.args && job.args.length > 0
                      ? job.args.join(', ')
                      : 'None'}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        ) : (
          !loading && <Text>No jobs scheduled currently.</Text>
        )}
      </Box>
    );
  };
  
  export default BackgroundJobsView;